import { cn } from "@shadcn/lib/utils";
import { Link } from "@tanstack/react-router";
import { ChevronRight, type LucideIcon } from "lucide-react";

type SupportStepRowProps = {
	icon: LucideIcon;
	title: React.ReactNode;
	description: React.ReactNode;
	to?: string;
	onClick?: () => void;
	linkProps?: React.AnchorHTMLAttributes<HTMLAnchorElement>;
	showChevron?: boolean;
	className?: string;
};

const content = (
	props: Omit<SupportStepRowProps, "to" | "onClick" | "linkProps" | "showChevron" | "className"> & { showChevron?: boolean }
) => {
	const { icon: Icon, title, description, showChevron } = props;

	return (
		<>
			<div className="flex shrink-0 items-center justify-center">
				<Icon className="size-6" />
			</div>
			<div className="min-w-0 flex-1">
				<p className="font-breit text-base leading-none text-card-foreground">{title}</p>
				<p className="mt-1 text-sm leading-5 text-muted-foreground">{description}</p>
			</div>
			{showChevron ? <ChevronRight className="size-5 shrink-0 self-center" /> : null}
		</>
	);
};

export const SupportStepRow = (props: SupportStepRowProps) => {
	const { to, onClick, linkProps, showChevron = Boolean(to || onClick || linkProps), className } = props;
	const sharedClassName = cn(
		"border-border flex w-full items-start gap-4 border-t py-5 text-left transition-colors",
		(to || onClick || linkProps) && "cursor-pointer hover:bg-accent/20",
		className
	);
	const rowContent = content({ ...props, showChevron });

	if (to) {
		return (
			<Link to={to} className={sharedClassName}>
				{rowContent}
			</Link>
		);
	}

	if (onClick) {
		return (
			<button type="button" className={sharedClassName} onClick={onClick}>
				{rowContent}
			</button>
		);
	}

	if (linkProps) {
		return (
			<a className={sharedClassName} {...linkProps}>
				{rowContent}
			</a>
		);
	}

	return <div className={sharedClassName}>{rowContent}</div>;
};
